import React, { createContext, useState, useEffect, ReactNode } from 'react';
import axios from 'axios';
import { getApiUrl } from '../utils/api';

// Define types
export interface User {
  id: string;
  name: string;
  email: string;
  avatar?: string;
  role: string;
}

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  loading: boolean;
  error: string | null;
  login: (email: string, password: string) => Promise<boolean>;
  register: (name: string, email: string, password: string, activationCode?: string) => Promise<boolean>;
  loginWithGoogle: (tokenId: string, activationCode?: string) => Promise<boolean>;
  checkDomainRequiresActivation: (email: string) => Promise<boolean>;
  verifyActivationCode: (code: string) => Promise<{valid: boolean, organization?: string}>;
  logout: () => void;
  clearError: () => void;
}

const defaultContext: AuthContextType = {
  user: null,
  isAuthenticated: false,
  loading: true,
  error: null,
  login: async () => false,
  register: async () => false,
  loginWithGoogle: async () => false,
  checkDomainRequiresActivation: async () => false,
  verifyActivationCode: async () => ({ valid: false }),
  logout: () => {},
  clearError: () => {},
};

// Create context
export const AuthContext = createContext<AuthContextType>(defaultContext);

// Provider component
export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Load user on initial render if token exists
  useEffect(() => {
    const loadUser = async () => {
      // Check if we have a token
      const token = localStorage.getItem('token');
      if (!token) {
        setLoading(false);
        return;
      }

      try {
        // Set auth headers
        setAuthToken(token);
        
        // Get user data
        const res = await axios.get(getApiUrl('auth/me'));
        setUser(res.data);
        setIsAuthenticated(true);
      } catch (err) {
        console.error('Failed to load user:', err);
        localStorage.removeItem('token');
        setError('Authentication failed. Please login again.');
      } finally {
        setLoading(false);
      }
    };

    loadUser();
  }, []);

  // Helper to set auth token in headers
  const setAuthToken = (token: string | null) => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    } else {
      delete axios.defaults.headers.common['Authorization'];
    }
  };

  // Check if email domain requires activation code
  const checkDomainRequiresActivation = async (email: string): Promise<boolean> => {
    try {
      const res = await axios.post(getApiUrl('auth/check-domain'), { email });
      return res.data.requires_activation;
    } catch (err) {
      console.error('Error checking domain:', err);
      return true; // Default to requiring activation if check fails
    }
  };

  // Verify activation code
  const verifyActivationCode = async (code: string): Promise<{valid: boolean, organization?: string}> => {
    try {
      const res = await axios.post(getApiUrl('auth/verify-activation-code'), { 
        activation_code: code 
      });
      return { 
        valid: true, 
        organization: res.data.organization 
      };
    } catch (err) {
      return { valid: false };
    }
  };

  // Register user
  const register = async (name: string, email: string, password: string, activationCode?: string): Promise<boolean> => {
    try {
      setLoading(true);
      setError(null);
      
      // Check if domain requires activation
      const requiresActivation = await checkDomainRequiresActivation(email);
      
      // If activation required but no code provided
      if (requiresActivation && !activationCode) {
        setError('Your email domain requires an activation code');
        return false;
      }
      
      // Create registration data
      const registrationData: any = {
        name,
        email,
        password
      };
      
      // Add activation code if provided
      if (activationCode) {
        registrationData.activation_code = activationCode;
      }
      
      const res = await axios.post(getApiUrl('auth/register'), registrationData);
      
      // Save token & set auth
      const { token } = res.data;
      localStorage.setItem('token', token);
      setAuthToken(token);
      
      // Get user data
      const userRes = await axios.get(getApiUrl('auth/me'));
      setUser(userRes.data);
      setIsAuthenticated(true);
      
      return true;
    } catch (err: any) {
      console.error('Registration error:', err);
      setError(err.response?.data?.detail || err.response?.data?.message || 'Registration failed. Please try again.');
      return false;
    } finally {
      setLoading(false);
    }
  };

  // Login user
  const login = async (email: string, password: string): Promise<boolean> => {
    try {
      setLoading(true);
      setError(null);
      
      const res = await axios.post(getApiUrl('auth/login'), {
        email,
        password
      });
      
      // Save token & set auth
      const { token } = res.data;
      localStorage.setItem('token', token);
      setAuthToken(token);
      
      // Get user data
      const userRes = await axios.get(getApiUrl('auth/me'));
      setUser(userRes.data);
      setIsAuthenticated(true);
      
      return true;
    } catch (err: any) {
      console.error('Login error:', err);
      setError(err.response?.data?.detail || err.response?.data?.message || 'Invalid credentials. Please try again.');
      return false;
    } finally {
      setLoading(false);
    }
  };

  // Login with Google
  const loginWithGoogle = async (tokenId: string, activationCode?: string): Promise<boolean> => {
    try {
      setLoading(true);
      setError(null);
      
      // Prepare request payload
      const payload: any = { tokenId };
      
      // Add activation code if provided
      if (activationCode) {
        payload.activation_code = activationCode;
      }
      
      const res = await axios.post(getApiUrl('auth/google'), payload);
      
      // Save token & set auth
      const { token } = res.data;
      localStorage.setItem('token', token);
      setAuthToken(token);
      
      // Get user data
      const userRes = await axios.get(getApiUrl('auth/me'));
      setUser(userRes.data);
      setIsAuthenticated(true);
      
      return true;
    } catch (err: any) {
      console.error('Google login error:', err);
      // Special case for domain requires activation
      if (err.response?.status === 403 && err.response?.data?.detail?.includes('activation code')) {
        setError('Your email domain requires an activation code');
      } else {
        setError(err.response?.data?.detail || err.response?.data?.message || 'Google login failed. Please try again.');
      }
      return false;
    } finally {
      setLoading(false);
    }
  };

  // Logout user
  const logout = () => {
    // Remove token
    localStorage.removeItem('token');
    setAuthToken(null);
    
    // Clear vault projects to prevent data leakage between users
    localStorage.removeItem('vault_projects');
    
    // Reset state
    setUser(null);
    setIsAuthenticated(false);
    setError(null);
  };

  // Clear errors
  const clearError = () => {
    setError(null);
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated,
        loading,
        error,
        login,
        register,
        loginWithGoogle,
        checkDomainRequiresActivation,
        verifyActivationCode,
        logout,
        clearError
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

// Custom hook for easy context access
export const useAuth = () => React.useContext(AuthContext);